import { Component, h, State, Prop, Watch, Listen } from '@stencil/core';
import '../../../stories/Components/TestFooter/TestFooter';
import '../../../stories/Components/Toaster/Toaster';
import '../../../stories/Components/AttachmentBox/AttachmentPreview/AttachmentPreview';
import ChatSDK from 'test-iso-chat-sdk';
import { getDeviceID, getGroupcastUrlQuery } from '../../../lib/helpers';
import '../../../stories/Components/ReplyBlock/ReplyBlock';
import { showToast } from '../../../stories/Components/Toaster/toast-helper';
import { postGroupCastMessage, getPresignedUrlForGroupcastMedia } from '../../../api/groupcast.js';

interface MediaPreview {
  type: string;
  src: string;
  file: File;
}
interface MentionedUser {
  wordCount: number;
  userId: string;
  order: number;
}

interface ConversationConfig {
  pushNotifications: boolean;
  readEvents: boolean;
  typingEvents: boolean;
}

interface Member {
  isAdmin: boolean;
  lastMessageSentAt: number;
  lastSeen: number;
  metaData: {
    assetList?: Array<Record<string, string>>;
    country?: string;
    [key: string]: any;
  };
  online: boolean;
  userId: string;
  userIdentifier: string;
  userName: string;
  userProfileImageUrl: string;
}

interface LastReadAt {
  [userId: string]: number;
}

interface UsersOwnDetails {
  isAdmin: boolean;
  lastMessageSentAt: number;
  memberId: string;
}

interface ConversationDetails {
  adminCount: number;
  config: ConversationConfig;
  conversationImageUrl: string;
  conversationTitle: string;
  conversationType: number; // 1 = group, possibly 0 = direct
  createdAt: number;
  createdBy: string;
  createdByUserImageUrl: string;
  createdByUserName: string;
  customType: string | null;
  hiddenForCreator: boolean;
  isGroup: boolean;
  lastMessageSentAt: number;
  lastReadAt: LastReadAt;
  members: Member[];
  membersCount: number;
  metaData: Record<string, any> | null;
  opponentDetails: Record<string, any>;
  privateOneToOne: boolean;
  searchableTags: string[];
  updatedAt: number;
  usersOwnDetails: UsersOwnDetails;
}

@Component({
  tag: 'chat-footer',
  shadow: true,
  assetsDirs: ['assets'],
  styles: `
    .chat-footer-container {
      position: relative;
    }
    .toaster {
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 1000;
    }
  `,
})
class ChatFooter {
  @State() messageBody = '';
  @State() attachmentDetails = null;
  @State() deviceId: string = '';
  @State() mediaPreviews: MediaPreview[] = [];
  @State() inputKey: number = 0;
  @State() showCloseIcon: Boolean = false;
  @State() mentionUserList: MentionedUser[] = [];

  @Prop() userMetaData: any;
  @Prop() refetchMessages: () => void;
  @Prop() changeMessageSendingState: (newState: boolean) => void;
  @Prop() changeScrollToBottomState: (newState: boolean) => void;
  @Prop() updateAllMessages: (newMessages: any) => void;
  @Prop() allMessages: any;
  @Prop() selectedConversationId: string;
  @Prop({ mutable: true }) replyMessage: any;
  @Prop() handleremoveReplyMessage: () => void;
  @Prop() sortConversationsByLatestMessage: (conversations: any[]) => void;
  @Prop({ mutable: true }) conversationDetails: ConversationDetails = {} as ConversationDetails;
  @Prop({ mutable: true }) allConversationList: any = [];
  @Prop() updateConversationListProp: (updatedList: any[]) => void;

  @State() showCamera: Boolean = false; // Add this state to control camera visibility
  @State() linkPreview: String = '';
  @State() linkToPreview: String = '';
  @State() detectLinks: Boolean = true;
  @State() isGroupcast: Boolean = false;
  @State() groupcastId: String = '';

  private inputRef!: HTMLElement & { focusInput: () => void };
  private debounceTimeout: any;

  async componentWillLoad() {
    const deviceId = await getDeviceID();
    this.deviceId = deviceId;
    const {isGroupcast, groupcastId} = await getGroupcastUrlQuery();
    this.isGroupcast = isGroupcast;
    this.groupcastId = groupcastId;
  }

  handleInputChange =async (event: any) => {
    this.messageBody = event.detail.value;
   if(!this.isGroupcast) {
    this.debounceApiCall();
   }
  };

  debounceApiCall = () => {
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.postTyping();
    }, 300);
  };
  handleCameraClick = () => {
    this.showCamera = true; // Use state instead of directly accessing ref
  };
  postTyping = async () => {
    await ChatSDK.getInstance().message.postIndicateTyping({ conversationId: this.selectedConversationId });
  };

  generateSearchableTags = (body: string): string[] => {
    const words = body.trim().split(/\s+/);
    if (words.length <= 4) {
      return words;
    }

    const tags = [];
    tags.push(words[0]);
    tags.push(words[words.length - 1]);

    const mid1 = Math.floor(words.length / 3);
    const mid2 = Math.floor((2 * words.length) / 3);
    tags.push(words[mid1]);
    tags.push(words[mid2]);

    return tags;
  };

  handleFileUpload = (event: CustomEvent) => {
    const { files } = event.detail;
    this.attachmentDetails = files;
    this.mediaPreviews = [];

    Array.from(files).forEach((file: File) => {
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        const fileType = file.type.split('/')[0];
        if (['image', 'video', 'application', 'audio'].includes(fileType)) {
          this.mediaPreviews = [
            ...this.mediaPreviews,
            {
              type: fileType,
              src: e.target?.result as string,
              file,
            },
          ];
        }
      };
      reader.readAsDataURL(file);
    });
  };
  async updateConversationListOnMessageSend(localMessage: any) {
    // Ensure we have a valid conversation ID
    if (!this.selectedConversationId) {
      console.error('No selected conversation ID');
      return;
    }

    // Find if conversation exists
    const existingConversationIndex = this.allConversationList.findIndex(conversation => conversation.conversationId === this.selectedConversationId);

    // Prepare the message details
    const messageDetails = {
      body: localMessage.body,
      updatedAt: localMessage.sentAt || Date.now(),
      userName: this.userMetaData?.userName,
      userProfileImageUrl: this.userMetaData?.userProfileImageUrl || '',
      isDeliveredToAllMembers: false,
      isReadByAllMembers: false,
      isPendingAllMembers: true,
      senderId: this.userMetaData?.userId,
      sentAt: localMessage.sentAt || Date.now(),
      messageId: localMessage.messageId,
      attachments: localMessage.attachments || [],
      senderName: this.userMetaData?.userName,
    };

    // Create a new conversation list
    let updatedConversationList = [...this.allConversationList];

    if (existingConversationIndex !== -1) {
      // Update existing conversation
      updatedConversationList[existingConversationIndex] = {
        ...updatedConversationList[existingConversationIndex],
        lastMessageDetails: messageDetails,
      };
    } else {
      // Create new conversation
      const newConversation = {
        conversationId: this.selectedConversationId,
        lastMessageDetails: messageDetails,
        isGroup: this.conversationDetails?.conversationType === 1,
        conversationTitle: this.conversationDetails?.conversationTitle || '',
        conversationImageUrl: this.conversationDetails?.conversationImageUrl || '',
        opponentDetails: this.conversationDetails?.opponentDetails || null,
      };
      updatedConversationList.unshift(newConversation);
    }

    // Verify the prop method exists and is a function
    if (typeof this.updateConversationListProp === 'function') {
      try {
        // Call the prop method and log the result
        this.updateConversationListProp(updatedConversationList);
      } catch (error) {
        console.error('Error updating conversation list', error);
      }
    } else {
      console.warn('updateConversationListProp is not a function', this.updateConversationListProp);
    }
  }

  createLocalMessage = (messageBody: string, attachments = null, messageId: string) => ({
    body: messageBody,
    messageId: messageId,
    deliveredToAll: 'pending',
    senderId: this.userMetaData.userId,
    messageType: this.replyMessage ? 2 : 0,
    sentAt: Date.now(),
    ...(attachments && { attachments }),
    ...(this.mentionUserList?.length > 0 && { mentionedUsers: this.mentionUserList }),
    ...(this.replyMessage && { metaData: this.getReplyMetadata() }),
  });

  updateMessageWithApiResponse = (messageId: string, oldMessageId: string) => {
    const newMessages = [...this.allMessages];
    const indexOfLastMessage = newMessages.findIndex(message => message.messageId === oldMessageId);
    if (indexOfLastMessage !== -1) {
      const lastMessage = newMessages[indexOfLastMessage];
      const lastNewMessage = { ...lastMessage, messageId, deliveredToAll: false };
      newMessages.splice(indexOfLastMessage, 1, lastNewMessage);
      this.updateAllMessages(newMessages);
    }
  };
  clearLinkPreview() {
    this.linkPreview = '';
    this.linkToPreview = '';
  }
  handleSendMessage = async () => {
    if (!this.messageBody.trim() && !this.attachmentDetails) {
      const singleLineText = this.messageBody.replace(/\n/g, ' ');
      this.messageBody = singleLineText;
      return;
    }
    this.handleremoveReplyMessage();
    this.clearLinkPreview();
    this.changeScrollToBottomState(true);
    const conversationId = this.selectedConversationId;
    const body = this.messageBody.trim();

    if (body && !this.attachmentDetails) {
      await this.sendTextMessage(body, conversationId);
    } else if (this.attachmentDetails && Array.isArray(this.attachmentDetails)) {
      await this.sendMediaMessage(conversationId);
    }
    this.detectLinks = true;
    this.inputRef?.focusInput();
  };

  handleDirectAudioUpload = async (audioFile: File) => {
    this.changeMessageSendingState(true);
    const conversationId = this.selectedConversationId;
    let messageId = Date.now().toString() + Math.random() * 100;

    const attachment = {
      nameWithExtension: audioFile.name,
      mediaType: 2, // Audio type
      mediaId: audioFile.lastModified + '',
      mimeType: audioFile.type,
      size: audioFile.size,
      file: audioFile,
    };

    const localAttachment = {
      name: audioFile.name,
      size: audioFile.size,
      type: 'audio',
      mediaUrl: URL.createObjectURL(audioFile),
      mimeType: audioFile.type,
    };

    const localMessage = this.createLocalMessage('AttachmentMessage:Multiple', [localAttachment], messageId);
    try {
      await this.updateConversationListOnMessageSend(localMessage);
    } catch (error) {
      console.error('Error updating conversation list', error);
    }
    this.updateAllMessages([...this.allMessages, localMessage]);

    try {
      let resForPresignedUrl;
      if(this.isGroupcast) {
        resForPresignedUrl = await getPresignedUrlForGroupcastMedia(this.userMetaData?.userIdentifier, attachment.mimeType)

      } else {
        resForPresignedUrl = await ChatSDK.getInstance().message.postMessagesPresignedurls({
          conversationId: conversationId,
          attachments: [attachment],
        });
      }
    

      if (resForPresignedUrl?.presignedUrls){
        await this.uploadAndSendMedia(resForPresignedUrl.presignedUrls, [attachment], conversationId, messageId);
      } else if (resForPresignedUrl?.presignedUrl){
        await this.uploadAndSendMedia([resForPresignedUrl], [attachment], conversationId, messageId);
      
      }
    } catch (error) {
      console.error(error, 'Error while uploading the audio');
      showToast({
        message: error?.message,
        type: 'error',
        position: 'bottom-right',
      });
    } finally {
      this.changeMessageSendingState(false);
    }
  };

  async sendTextMessage(body: string, conversationId: string) {
    this.messageBody = '';
    let messageId = 'localId' + Date.now().toString() + Math.random() * 100;
    const localMessage = this.createLocalMessage(body, null, messageId);
    try {
      await this.updateConversationListOnMessageSend(localMessage);
    } catch (error) {
      console.error('Error updating conversation list', error);
    }
    this.updateAllMessages([...this.allMessages, localMessage]);
    this.changeMessageSendingState(true);

    const urlParams = new URLSearchParams(window.location.search);
    const isGroupcast = urlParams.has('groupcast');

    const groupcastId = urlParams.get('groupcast');

    if (isGroupcast) {
      const payloadForGroupcastMessage = {
        showInConversation: true,
        sendPushForNewConversationCreated: true,
        searchableTags: this.generateSearchableTags(body),
        metaData: this.getReplyMetadata(),
        body: body,
        groupcastId: groupcastId,
        readCount: 1,
        notifyOnCompletion: true,
        messageType: this.replyMessage ? 2 : 0,
        hideNewConversationsForSender: true,
        encrypted: true,
        deviceId: this.deviceId,
        deliveryCount: 1,
      };

      try {
        const res = await postGroupCastMessage(payloadForGroupcastMessage);
        this.updateMessageWithApiResponse(res.messageId, messageId);
      } catch (error) {
        console.error(error, 'Error while sending groupcast message');
        showToast({
          message: error?.message,
          type: 'error',
          position: 'bottom-right',
        });
      }
    } else {
      const payload = {
        showInConversation: true,
        messageType: this.replyMessage ? 2 : 0,
        parentMessageId: this.replyMessage ? this.replyMessage.messageId : undefined,
        searchableTags: this.generateSearchableTags(body),
        encrypted: false,
        conversationId: conversationId,
        deviceId: this.deviceId,
        customType: body?.match(/(https?:\/\/[^\s]+)/g) ? 'AttachmentMessage:Link' : 'AttachmentMessage:Text',
        body: body,
        metaData: this.getReplyMetadata(),
      };

      if (this.mentionUserList?.length > 0) {
        payload['mentionedUsers'] = this.mentionUserList;
      }
      try {
        const res = await ChatSDK.getInstance().message.postMessages(payload);
        this.updateMessageWithApiResponse(res.messageId, messageId);
      } catch (error) {
        console.error(error, 'postMessages error for text');
        showToast({
          message: error?.message,
          type: 'error',
          position: 'bottom-right',
        });
      } finally {
        this.changeMessageSendingState(false);
        this.replyMessage = null;
        this.messageBody = '';
      }
    }
  }

  async sendMediaMessage(conversationId: string) {
    this.changeMessageSendingState(true);
    let messageId = Date.now().toString() + Math.random() * 100;
    const attachments = this.attachmentDetails.map(file => ({
      nameWithExtension: file.name,
      mediaType: file.type.startsWith('image/') ? 0 : file.type.startsWith('video/') ? 1 : file.type.startsWith('audio/') ? 2 : 3,
      mediaId: file.lastModified + '',
      mimeType: file.type,
      size: file.size,
      file,
    }));

    const localAttachments = this.mediaPreviews.map(preview => ({
      name: preview.file.name,
      size: preview.file.size,
      type: preview.type,
      mediaUrl: preview.src,
      mimeType: preview.file.type,
    }));
    const localMessage = this.createLocalMessage(this.messageBody || 'AttachmentMessage:Multiple', localAttachments, messageId);
    try {
      await this.updateConversationListOnMessageSend(localMessage);
    } catch (error) {
      console.error('Error updating conversation list', error);
    }

    this.updateAllMessages([...this.allMessages, localMessage]);
    this.handleClearAttachment();
    let payload = {
      conversationId: conversationId,
      attachments: attachments,
    };
    try {

      let resForPresignedUrl;
      if(this.isGroupcast) {
        resForPresignedUrl =await getPresignedUrlForGroupcastMedia(this.userMetaData?.userIdentifier, attachments?.mimeType);
      } else {
        resForPresignedUrl = await ChatSDK.getInstance().message.postMessagesPresignedurls(payload);
      }

      if (resForPresignedUrl?.presignedUrls ) {
        await this.uploadAndSendMedia(resForPresignedUrl.presignedUrls, attachments, conversationId, messageId);
      } else if (resForPresignedUrl?.presignedUrl) {
        await this.uploadAndSendMedia([resForPresignedUrl], attachments, conversationId, messageId);
        
      }
    } catch (error) {
      console.error(error, 'Error while uploading the media for Presignedurls');
      showToast({
        message: error?.message,
        type: 'error',
        position: 'bottom-right',
      });
    } finally {
      this.changeMessageSendingState(false);
      this.messageBody = '';
    }
  }

  async uploadAndSendMedia(presignedUrls: any[], attachments: any[], conversationId: string, oldMessageId: string) {
    const allPayloads = [];

    const uploadPromises = presignedUrls.map(async (urlDetails, index) => {
      let mediaId, mediaUrl, thumbnailUrl, mediaPresignedUrl
      if(this.isGroupcast) {
        mediaUrl =  urlDetails?.mediaUrl;
        mediaPresignedUrl = urlDetails?.presignedUrl;
        mediaId = urlDetails?.mediaUrl;
        thumbnailUrl = urlDetails?.mediaUrl;
      } else {
        ({ mediaPresignedUrl, mediaId, mediaUrl, thumbnailUrl } = urlDetails);
      }
      const attachmentPayload = attachments[index];
      const fileExtensionType = attachmentPayload.mimeType.split('/')[1];
      const newPayload = {
        mediaId,
        mediaUrl,
        thumbnailUrl,
        mediaType: attachmentPayload.mediaType,
        nameWithExtension: attachmentPayload.nameWithExtension,
        name: attachmentPayload.nameWithExtension,
        mimeType: attachmentPayload.mimeType,
        extension: fileExtensionType,
        size: attachmentPayload.size,
        attachmentType: 0,
      };

      allPayloads.push(newPayload);

      await fetch(mediaPresignedUrl, {
        method: 'PUT',
        body: attachmentPayload.file,
        headers: {
          'Content-Type': attachmentPayload.mimeType,
        },
      });
    });

    await Promise.all(uploadPromises);
    let payload = {
      showInConversation: true,
      messageType: this.replyMessage ? 2 : 0,
      parentMessageId: this.replyMessage?.messageId,
      encrypted: false,
      conversationId,
      body: this.messageBody || 'AttachmentMessage:Multiple',
      customType: attachments?.[0]?.mimeType?.startsWith('application/') ? 'AttachmentMessage:Doc' : 'AttachmentMessage:Media',
      attachments: allPayloads,
      deviceId: this.deviceId,
      metaData: this.getReplyMetadata(),
    };
    let payloadForGroupcastMessage = {...payload, 
      hideNewConversationsForSender: false,
      groupcastId: this.groupcastId,
      notifyOnCompletion: true,
      sendPushForNewConversationCreated: true
    }
    if (this.mentionUserList?.length > 0) {
      payload['mentionedUsers'] = this.mentionUserList;
    }
    try {
      let sendMessageRes;
      if(this.isGroupcast) {
        sendMessageRes = await postGroupCastMessage(payloadForGroupcastMessage);
      } else {
        sendMessageRes = await ChatSDK.getInstance().message.postMessages(payload);

      }

      this.updateMessageWithApiResponse(sendMessageRes.messageId, oldMessageId);
      this.replyMessage = null;
      showToast({
        message: 'Media sent successfully!',
        type: 'success',
        position: 'bottom-right',
      });
    } catch (error) {
      console.error(error, 'Error while sending the message');
      showToast({
        message: error?.message,
        type: 'error',
        position: 'bottom-right',
      });
    }
  }

  getReplyMetadata() {
    return {
      parentMessageId: this.replyMessage?.messageId,
      parentMessage: this.replyMessage?.body,
      parentMessageTime: this.replyMessage?.sentAt,
      parentMessageSenderId: this.replyMessage?.senderInfo?.userId,
      parentMessageSenderName: this.replyMessage?.senderInfo?.userName,
      ...(this.replyMessage?.attachments?.[0] && {
        parentMessageName: this.replyMessage.attachments[0].name,
        parentMessageMediaUrl: this.replyMessage.attachments[0].mediaUrl,
        parentMessageType: this.replyMessage.attachments[0].mimeType,
      }),
    };
  }

  handleClearAttachment = () => {
    this.attachmentDetails = null;
    this.mediaPreviews = [];
    this.showCamera = false;
  };

  @Watch('selectedConversationId')
  async handleDataChange() {
    this.handleClearAttachment();
  }

  @Watch('replyMessage')
  replyFocusHandler() {
    if (this.inputRef && this.replyMessage) {
      this.inputRef?.focusInput();
    }
  }
  @Listen('mention-user')
  handleMentionUser(event: CustomEvent) {
    const { value, mentionUsersList } = event.detail;
    this.messageBody = value;
    this.mentionUserList = mentionUsersList;
  }

  @Listen('send-qr-code-message')
  handleSendQrCodeMessage(event: CustomEvent) {
    const { message, conversationId } = event.detail;
    
    // Check if this is for the current conversation
    if (conversationId === this.selectedConversationId) {
      // Set the message body to the QR code message
      this.messageBody = message;
      
      // Send the message
      this.handleSendMessage();
    } else {
      console.log('Conversation ID mismatch:', conversationId, 'vs', this.selectedConversationId);
    }
  }

  @Listen('qr-code-message-ready')
  handleQrCodeMessageReady(event: CustomEvent) {
    console.log('Received qr-code-message-ready event:', event.detail);
    const { message, conversationId } = event.detail;
    
    // Check if this is for the current conversation
    if (conversationId === this.selectedConversationId) {
      console.log('Setting message body and sending message via qr-code-message-ready');
      // Set the message body to the QR code message
      this.messageBody = message;
      
      // Send the message
      this.handleSendMessage();
    } else {
      console.log('Conversation ID mismatch in qr-code-message-ready:', conversationId, 'vs', this.selectedConversationId);
    }
  }

  handleCameraCapture = async (captureData: { file: File; type: string; src: string }) => {
    const customEvent = new CustomEvent('fileUploaded', {
      detail: {
        files: [captureData.file],
        fileTypes: [captureData.type],
        totalFiles: 1,
      },
    });
    this.handleFileUpload(customEvent);
    this.showCamera = false;
  };

  render() {
    const contentType = this.attachmentDetails ? this.attachmentDetails.map(detail => detail?.type?.split('/')[0] || '') : [];
    const currentPreviews = this.mediaPreviews.filter(preview => contentType.includes(preview.type)).map(preview => preview.src);
    return (
      <div class="chat-footer-container">
        {this.attachmentDetails || this.showCamera ? (
          <attachment-preview
            showCamera={this.showCamera}
            onClose={this.handleClearAttachment}
            width="150px"
            height="150px"
            bgColor="var(--bg-primary-light)"
            padding="3px"
            margin="10px 0 0 0"
            contentType={contentType}
            content={this.mediaPreviews}
            onSend={this.handleSendMessage}
            onfile-uploaded={this.handleFileUpload} // Add this to handle camera captures
            key={this.inputKey}
            inputValue={this.messageBody}
            oninput-change={this.handleInputChange}
            backgroundColor="var(--bg-primary-color)"
            containerBoxShadow="none"
            inputFontColor="var(--text-primary-color)"
            inputBorderRadius="10px"
            inputPlaceholderColor="var(--text-primary-color)"
            conversationDetails={this.conversationDetails}
            userMetaData={this.userMetaData}
          ></attachment-preview>
        ) : (
          <my-test-footer
            key={this.inputKey}
            inputValue={this.messageBody}
            onFileUploaded={this.handleFileUpload}
            oninput-change={this.handleInputChange}
            onSendClick={this.handleSendMessage}
            backgroundColor="var(--bg-primary-color)"
            containerBoxShadow="none"
            inputFontColor="var(--text-primary-color)"
            showCross={this.attachmentDetails ? true : false}
            contentType={contentType}
            content={currentPreviews}
            onClose={this.handleClearAttachment}
            ref={el => (this.inputRef = el)}
            replyMessage={this.replyMessage}
            handleremoveReplyMessage={this.handleremoveReplyMessage}
            conversationDetails={this.conversationDetails}
            userMetaData={this.userMetaData}
            onCameraClick={this.handleCameraClick}
            linkPreview={this.linkPreview}
            linkToPreview={this.linkToPreview}
            detectLinks={this.detectLinks}
            handleDirectAudioUpload={this.handleDirectAudioUpload}
            selectedConversationId={this.selectedConversationId}
          ></my-test-footer>
        )}
      </div>
    );
  }
}

export { ChatFooter };
